import { FieldRules, ReportFormRuleTemplate, RuleContext, RuleNode } from '@navstation-portal/reporting/data-model/ui';
import {
  MISSING_EXPECTED,
  NamedRuleExplanation,
  RuleExplanation,
  evaluate,
  explainRule,
  formatValue,
  resolveLabel,
} from '@navstation-portal/reporting/util/form-rules';

// Splits a field path into the table that owns the column and the column
// itself. A plain id, 'ifo', belongs to the section. A two-part id,
// 'heating.ifo', belongs to that table of the section. Nothing else is a path:
// an id with more parts names no table, so it reads as a plain id and finds
// nothing, which is what a typo should do.
export const splitFieldPath = (fieldPath: string): { tableId: string | null; fieldId: string } => {
  const parts = fieldPath.split('.');
  return parts.length === 2 ? { tableId: parts[0], fieldId: parts[1] } : { tableId: null, fieldId: fieldPath };
};

// The rules for one field. A table owns its columns, so a column reads its
// table and nothing else, and `section.fields` holds the fields that belong to
// no table. Two tables that draw the same column hold an entry each, which is
// what lets them differ.
export const findField = (
  template: ReportFormRuleTemplate | null,
  tabId: string,
  sectionId: string,
  fieldPath: string
): FieldRules | undefined => {
  const section = template?.tabs?.[tabId]?.sections?.[sectionId];
  const { tableId, fieldId } = splitFieldPath(fieldPath);
  return tableId === null ? section?.fields?.[fieldId] : section?.tables?.[tableId]?.fields?.[fieldId];
};

// True when the user can see the field: its tab shows, its section shows, the
// table that draws it shows, and the field itself shows. Each rule fails open,
// so a missing rule shows the item, the same answer the visibility methods
// give. The store and the stale-status check both use it.
export const isFieldOnScreen = (
  template: ReportFormRuleTemplate | null,
  context: RuleContext,
  tabId: string,
  sectionId: string,
  fieldPath: string
): boolean => {
  const tab = template?.tabs?.[tabId];
  const section = tab?.sections?.[sectionId];
  const { tableId } = splitFieldPath(fieldPath);
  const rules = [
    tab?.isTabVisible,
    section?.isSectionVisible,
    tableId === null ? undefined : section?.tables?.[tableId]?.isTableVisible,
    findField(template, tabId, sectionId, fieldPath)?.isFieldVisible,
  ];
  return rules.every((rule) => (rule ? evaluate(rule, context) : true));
};

/**
 * Evaluates an optional rule, or gives the default a missing rule takes. The
 * two locks both fail open, so the debug block reads them the same way the
 * store methods do.
 */
export const evaluateOr = (rule: RuleNode | undefined, context: RuleContext, missing: boolean): boolean =>
  rule ? evaluate(rule, context) : missing;

// DEV-ONLY. The debug explanation of a rule that does not apply, for example
// the required rule of a hidden field. It says why in place of the rule tree.
export const blockedExplanation = (detail: string): RuleExplanation => ({
  result: false,
  missing: false,
  lines: [{ type: 'hidden', detail, result: false, depth: 0 }],
});

// The label of a field with no template label, as V1 makes it from the last
// part of the path: 'bilgeWater' gives 'Bilge Water'. A column of a table
// gives its own id, not the id of the table.
const titleCase = (fieldPath: string): string =>
  splitFieldPath(fieldPath)
    .fieldId.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^./, (first) => first.toUpperCase());

// The Field Warnings of one field, for the input to show. The expected-field
// message is separate, because only the input knows whether its value is
// empty. A table cell tests the value of its own row in this way.
export interface FieldWarnings {
  expectedMessage: string | null;
  warnings: string[];
}

// Why a field shows no warning at all, or null when it can show one. A field
// the user cannot see shows nothing, as it is never required. A disabled field
// shows nothing either. Angular takes a disabled control out of validation, so
// the required rule needs no check of its own, but a warning is not a
// validator.
const warningsBlockedBy = (
  template: ReportFormRuleTemplate | null,
  context: RuleContext,
  tabId: string,
  sectionId: string,
  fieldPath: string,
  field: FieldRules | undefined
): string | null => {
  if (!isFieldOnScreen(template, context, tabId, sectionId, fieldPath)) {
    return 'the field does not show, so it shows no warning';
  }
  if (evaluateOr(field?.isFieldDisabled, context, false)) {
    return 'the field is disabled, so it shows no warning';
  }
  return null;
};

// The text of the expected-field warning, and whether the field id gave the
// label because the template holds none.
const expectedText = (
  field: FieldRules,
  context: RuleContext,
  fieldPath: string
): { text: string; labelFromFieldId: boolean } => {
  // An empty text and a null from the database both mean no text. An empty
  // message would draw an icon with a blank line.
  if (field.expectedMessage) {
    return { text: field.expectedMessage, labelFromFieldId: false };
  }
  // An empty label tells the form to draw no label text. A message cannot
  // start with nothing, so it takes the field id.
  const label = resolveLabel(field.label, context);
  return { text: `${label || titleCase(fieldPath)} should be reported`, labelFromFieldId: !label };
};

export const resolveFieldWarnings = (
  template: ReportFormRuleTemplate | null,
  context: RuleContext,
  tabId: string,
  sectionId: string,
  fieldPath: string
): FieldWarnings => {
  const field = findField(template, tabId, sectionId, fieldPath);
  if (!field || warningsBlockedBy(template, context, tabId, sectionId, fieldPath, field) !== null) {
    return { expectedMessage: null, warnings: [] };
  }

  // Required wins over the expected message: the field already shows that it
  // is missing. A warning about the value still shows.
  const isExpected =
    !evaluateOr(field.isFieldRequired, context, false) && evaluateOr(field.isFieldExpected, context, false);

  // Template order. The map keeps the order of its keys as the server sends them.
  const warnings = Object.values(field.warnings ?? {})
    .filter((warning) => evaluate(warning.rule, context))
    .map((warning) => warning.message);

  return { expectedMessage: isExpected ? expectedText(field, context, fieldPath).text : null, warnings };
};

// DEV-ONLY. The Field Warnings of one field for the debug tooltip: the
// expected rule first, then one block for each warning, named by its id. Each
// block ends with the message it gives, so a reader sees the text and the rule
// together.
export const explainFieldWarnings = (
  template: ReportFormRuleTemplate | null,
  context: RuleContext,
  tabId: string,
  sectionId: string,
  fieldPath: string
): NamedRuleExplanation[] => {
  const field = findField(template, tabId, sectionId, fieldPath);
  const blockedBy = warningsBlockedBy(template, context, tabId, sectionId, fieldPath, field);
  const withMessage = (explanation: RuleExplanation, detail: string): RuleExplanation => ({
    ...explanation,
    lines: [...explanation.lines, { type: 'message', detail, result: explanation.result, depth: 0 }],
  });

  const expected = (): RuleExplanation => {
    if (blockedBy !== null) return blockedExplanation(blockedBy);
    if (evaluateOr(field?.isFieldRequired, context, false)) {
      return blockedExplanation('the field is required, so it shows no expected message');
    }
    const explanation = explainRule(field?.isFieldExpected, context, MISSING_EXPECTED);
    if (!field) return explanation;
    const { text, labelFromFieldId } = expectedText(field, context, fieldPath);
    return withMessage(
      explanation,
      labelFromFieldId ? `${formatValue(text)} (no template label, so the field id gives it)` : formatValue(text)
    );
  };

  return [
    { name: 'isFieldExpected', explanation: expected() },
    ...Object.entries(field?.warnings ?? {}).map(([id, warning]) => ({
      name: `warnings.${id}`,
      explanation:
        blockedBy !== null
          ? blockedExplanation(blockedBy)
          : withMessage(explainRule(warning.rule, context), formatValue(warning.message)),
    })),
  ];
};
